import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';

export const leaveTypeSchema = z.enum(['VACATION', 'SICK', 'DOCTOR']);
export type LeaveType = z.infer<typeof leaveTypeSchema>;

export const leaveStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type LeaveStatus = z.infer<typeof leaveStatusSchema>;

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  VACATION: 'Urlaub',
  SICK: 'Krankheit',
  DOCTOR: 'Arzt',
};

export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  PENDING: 'Ausstehend',
  APPROVED: 'Genehmigt',
  REJECTED: 'Abgelehnt',
};

const leaveRequestInputBase = z.object({
  type: leaveTypeSchema,
  /** Tag `YYYY-MM-TT`. Bei DOCTOR der einzelne Tag. */
  startDate: z.string().date(),
  /** Tag `YYYY-MM-TT`. Bei VACATION/SICK Pflicht; bei DOCTOR = startDate (serverseitig). */
  endDate: z.string().date().optional(),
  /** Nur DOCTOR: Start-/Endzeit am Tag (ISO-Zeitstempel). */
  startTime: z.string().datetime().optional(),
  endTime: z.string().datetime().optional(),
  /** Nur SICK: erfasste Stundenanzahl. */
  hours: z.number().positive().max(24).optional(),
  reason: z.string().trim().max(2000).optional(),
});

export const createLeaveRequestSchema = leaveRequestInputBase.superRefine((val, ctx) => {
  if (val.type === 'DOCTOR') {
    if (!val.startTime) ctx.addIssue({ path: ['startTime'], code: 'custom', message: 'Startzeit erforderlich.' });
    if (!val.endTime) ctx.addIssue({ path: ['endTime'], code: 'custom', message: 'Endzeit erforderlich.' });
    if (val.startTime && val.endTime && val.endTime <= val.startTime) {
      ctx.addIssue({ path: ['endTime'], code: 'custom', message: 'Endzeit muss nach der Startzeit liegen.' });
    }
  } else {
    if (!val.endDate) ctx.addIssue({ path: ['endDate'], code: 'custom', message: 'Enddatum erforderlich.' });
    if (val.endDate && val.endDate < val.startDate) {
      ctx.addIssue({ path: ['endDate'], code: 'custom', message: 'Enddatum darf nicht vor dem Startdatum liegen.' });
    }
  }
});
export type CreateLeaveRequestInput = z.infer<typeof createLeaveRequestSchema>;

/** Bearbeiten (nur PENDING). Felder optional; Server validiert/berechnet neu. */
export const updateLeaveRequestSchema = leaveRequestInputBase.partial();
export type UpdateLeaveRequestInput = z.infer<typeof updateLeaveRequestSchema>;

export const leaveRequestResponseSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  type: leaveTypeSchema,
  status: leaveStatusSchema,
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  startTime: z.string().datetime().nullable(),
  endTime: z.string().datetime().nullable(),
  days: z.number().int().nullable(),
  hours: z.number().nullable(),
  reason: z.string().nullable(),
  reviewerName: z.string().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  adminNote: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type LeaveRequestResponse = z.infer<typeof leaveRequestResponseSchema>;

/** Genehmigen/Ablehnen mit optionaler Admin-Notiz. */
export const reviewLeaveRequestSchema = z.object({
  adminNote: z.string().trim().max(2000).optional(),
});
export type ReviewLeaveRequestInput = z.infer<typeof reviewLeaveRequestSchema>;

/** Listen-Query (eigene + Admin): Pagination + Filter. */
export const leaveRequestsQuerySchema = listQuerySchema.extend({
  status: leaveStatusSchema.optional(),
  type: leaveTypeSchema.optional(),
  employeeId: z.string().uuid().optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  dateFrom: z.string().date().optional(),
  dateTo: z.string().date().optional(),
});
export type LeaveRequestsQuery = z.infer<typeof leaveRequestsQuerySchema>;

/** Kalender-Query: ein Monat. */
export const leaveCalendarQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});
export type LeaveCalendarQuery = z.infer<typeof leaveCalendarQuerySchema>;

/** Urlaubsanalyse: eine Zeile je Mitarbeiter. */
export const vacationAnalysisRowSchema = z.object({
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  annualVacationDays: z.number().int(),
  carryoverDays: z.number().int(),
  takenDays: z.number().int(),
  remainingDays: z.number().int(),
  sickDays: z.number().int(),
  doctorHours: z.number(),
});
export type VacationAnalysisRow = z.infer<typeof vacationAnalysisRowSchema>;

export const vacationAnalysisResponseSchema = z.object({
  year: z.number().int(),
  rows: z.array(vacationAnalysisRowSchema),
});
export type VacationAnalysisResponse = z.infer<typeof vacationAnalysisResponseSchema>;
